import { useEffect, useState } from "react";

// Module multilingue — langue courante du site public.
//
// Deux informations distinctes, volontairement séparées :
//
//   * la LANGUE DEMANDÉE par le visiteur (?lang= dans l'URL, ou son dernier
//     choix mémorisé). Elle est connue immédiatement, sans le moindre appel
//     réseau — c'est ce qui permet d'envoyer `lang` dès le tout premier appel
//     /theme/sections, sans attendre /theme/init. Le serveur ignore de
//     lui-même une langue qui n'est pas activée sur le thème, donc rien à
//     vérifier ici avant de la transmettre.
//   * la CONFIGURATION du thème (module actif ? quelles langues ?), qui
//     arrive avec /theme/init et ne sert qu'à l'affichage du sélecteur.
//
// Tant que l'admin n'a pas activé le module, la configuration reste à
// `enabled: false` : aucun sélecteur ne s'affiche, aucun paramètre de langue
// n'est ajouté aux appels, le site se comporte exactement comme avant.

const STORAGE_KEY = "site_lang";

// Sens d'écriture connu SANS attendre le serveur : l'attribut dir doit être
// posé avant le premier rendu, sinon une page arabe s'affiche brièvement de
// gauche à droite. Volontairement dupliqué de backend/services/i18n_service.py
// (SUPPORTED_LANGS) — trois lignes, contre un aller-retour réseau bloquant.
const DIRECTIONS = { fr: "ltr", en: "ltr", ar: "rtl" };

let _config = { enabled: false, default_lang: "fr", languages: [] };
const _listeners = new Set();

function readQueryLang() {
  try {
    return new URLSearchParams(window.location.search).get("lang") || null;
  } catch {
    return null;
  }
}

function readStoredLang() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    // Navigation privée / stockage bloqué : on retombe simplement sur la
    // langue d'origine du site, jamais une erreur.
    return null;
  }
}

/**
 * Langue demandée par ce visiteur, ou null s'il n'a rien demandé (le serveur
 * sert alors le contenu d'origine). L'URL prime sur le choix mémorisé : un
 * lien partagé vers "?lang=ar" doit s'ouvrir en arabe même chez quelqu'un qui
 * avait choisi l'anglais la veille.
 *
 * Volontairement NON filtrée par la configuration du thème : elle est lue
 * avant que /theme/init n'ait répondu. Une langue que la boutique ne propose
 * pas est ignorée par le serveur, qui renvoie alors le contenu d'origine.
 */
export function getLang() {
  const requested = readQueryLang() || readStoredLang();
  return requested && DIRECTIONS[requested] ? requested : null;
}

/**
 * Langue réellement AFFICHÉE, une fois la configuration du thème connue.
 *
 * Distincte de `getLang()` pour un cas précis : un visiteur qui avait choisi
 * l'arabe sur une boutique qui a depuis retiré cette langue (ou coupé le
 * module) garde "ar" dans son stockage local. Servir ce code tel quel
 * poserait dir="rtl" sur une page redevenue française, et n'activerait aucun
 * bouton du sélecteur. Tant que la configuration n'est pas arrivée, on garde
 * la langue demandée : c'est la seule façon d'éviter qu'une vraie page arabe
 * s'affiche une fraction de seconde de gauche à droite.
 */
export function resolvedLang() {
  const requested = getLang();
  const known = (_config.languages || []).map((l) => l.code);
  if (!known.length) return requested || _config.default_lang || "fr";
  if (_config.enabled && requested && known.includes(requested)) return requested;
  return _config.default_lang || "fr";
}

/**
 * Paramètre `lang` à joindre à un appel de sections. Objet vide quand le
 * visiteur n'a rien demandé — l'URL de l'API reste alors identique à celle
 * d'avant ce module, ce qui préserve les caches serveur existants.
 */
export function langParams() {
  const lang = getLang();
  return lang ? { lang } : {};
}

/** Pose lang/dir sur <html> — au chargement du module, puis à nouveau dès que
 * la configuration du thème est connue (elle peut corriger un choix devenu
 * caduc, voir resolvedLang). */
export function applyDocumentLang() {
  const lang = resolvedLang();
  try {
    document.documentElement.lang = lang;
    document.documentElement.dir = DIRECTIONS[lang] || "ltr";
  } catch {
    /* rendu hors navigateur (tests) : rien à poser */
  }
}

export function getLangConfig() {
  return _config;
}

/** Alimenté par App.js à la réception de /theme/init. */
export function setLangConfig(config) {
  _config = {
    enabled: !!config?.enabled,
    default_lang: config?.default_lang || "fr",
    languages: Array.isArray(config?.languages) ? config.languages : [],
  };
  applyDocumentLang();
  _listeners.forEach((fn) => fn(_config));
}

/** Configuration multilingue du thème, re-rendue dès qu'elle est connue. */
export function useLangConfig() {
  const [config, setConfig] = useState(_config);
  useEffect(() => {
    setConfig(_config);
    _listeners.add(setConfig);
    return () => _listeners.delete(setConfig);
  }, []);
  return config;
}

/**
 * Change la langue affichée.
 *
 * Recharge la page volontairement, plutôt que de tout re-rendre : les
 * sections sont mises en cache au niveau module (Home.jsx,
 * useTemplateSections.js, TemplateSections.jsx) et un rechargement est le
 * seul moyen fiable de garantir qu'aucun fragment de la langue précédente ne
 * subsiste à l'écran. Un changement de langue est une action rare et
 * délibérée : l'aller-retour est attendu par le visiteur.
 *
 * La langue est écrite DANS L'URL en plus du stockage local, pour qu'une page
 * traduite reste partageable telle quelle.
 */
export function setLang(code) {
  if (!code || !DIRECTIONS[code]) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* stockage indisponible : l'URL suffit à porter le choix */
  }
  try {
    const url = new URL(window.location.href);
    if (code === (_config.default_lang || "fr")) url.searchParams.delete("lang");
    else url.searchParams.set("lang", code);
    window.location.assign(url.toString());
  } catch {
    window.location.reload();
  }
}

applyDocumentLang();
